import React, { createContext, FC, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import useLocalStorageToken from '@lib/customHooks/useLocalStorageToken';
import { LoginLocation } from '@lib/routes/routes';
import authService from '@lib/services/auth';
import { GearUser, UserRoles } from '@lib/services/models';
import { useQuery } from '@lib/utils/use-query';

export const TOKEN_KEY = 'GEAR_TOKEN';

const useAuthState = () => {
  const [token, _setToken] = useLocalStorageToken();
  const [authenticated, setAuthenticated] = useState(!!token);
  const [currentUser, setCurrentUser] = useState<GearUser>();
  const [userRoles, setUserRoles] = useState<UserRoles>();

  const reset = async () => {
    if (!token) {
      setCurrentUser(undefined);
      setUserRoles(undefined);
      setAuthenticated(false);
      return;
    }

    const res = await authService.me();
    const { ok, data } = res;
    if (ok && data) {
      const { user, roles } = data;

      const _userRoles = roles.reduce<UserRoles>((all, item) => {
        all[item.role] = item.environments ?? [];
        return all;
      }, {});

      setCurrentUser(user);
      setUserRoles(_userRoles);
      setAuthenticated(!!res.data);
    } else {
      setCurrentUser(undefined);
      setUserRoles(undefined);
      setAuthenticated(false);
    }
  };

  const setToken = (newToken?: string) => {
    _setToken(newToken);
  };

  useEffect(() => {
    reset();
  }, [token]);

  return {
    token,
    authenticated,
    currentUser,
    userRoles,
    reset,
    setToken,
  };
};

export type UseAuth = ReturnType<typeof useAuthState>;

const authContext = createContext<UseAuth | null>(null);

const { Provider } = authContext;

export const AuthProvider: FC = ({ children }) => {
  const auth = useAuthState();
  const history = useHistory();
  const query = useQuery();

  const { authenticated, token } = auth;
  const absUrl = query.get('absUrl');

  useEffect(() => {
    const isLoginRoute = history.location.pathname === LoginLocation.path;

    const checkAuth = async () => {
      if (!authenticated && !isLoginRoute) {
        const next = encodeURIComponent(`${history.location.pathname}${history.location.search}`);
        history.push(`${LoginLocation.path}?next=${next}`);
      } else if (authenticated && isLoginRoute) {
        if (token && absUrl) {
          const res = await authService.me();

          if (res?.ok) {
            const url = new URL(absUrl);
            const params = new URLSearchParams(url.search);
            params.append('accessToken', token);
            window.location.href = `${absUrl}?${params.toString()}`;
          } else {
            const next = encodeURIComponent(
              `${history.location.pathname}${history.location.search}`,
            );
            history.push(`${LoginLocation.path}?next=${next}`);
          }
        } else {
          const next = decodeURIComponent(query.get('next') || '/');
          const [pathname, search] = next.split('?');
          history.push({ pathname, search });
        }
      }
    };

    checkAuth();
  }, [authenticated, absUrl]);

  return <Provider value={auth}>{children}</Provider>;
};

export const useAuth = () => {
  const auth = useContext(authContext);
  if (!auth) {
    throw new Error('useAuth must be used inside an AuthProvider');
  }

  return auth;
};
